import { useState } from "react";
import * as Lucide from "lucide-react";
import cn from "classnames";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AI_MODEL,
  AI_AGENT,
  AGENT_SUPPORTED_MODELS,
  useAiActionStore,
} from "@/store/zustand/aiActionStore";
import Icon, { ICONS } from "@/components/Core/Icons";
import { useCanvasId } from "@/utils/utils";
import useClaudeCodeSessions from "@/hooks/useClaudeCodeSessions";
import { useSubscriptionInfo } from "@/hooks/pricing/useSubscriptionInfo";
import { ClaudeAgentSection } from "./ClaudeAgentSection";
import { MenuItem } from "./ModelMenuItem";
import { MODEL_CONFIG_MAP } from "./modelConfig";

const ClaudeCodeBadge = () => (
  <span
    className="ml-1 text-[9px] rounded font-medium border border-[#2F3035] h-[12px] px-1 inline-flex items-center"
    style={{
      background:
        "linear-gradient(90deg, #38C793 0%, #937FF4 50%, #DA41D3 100%)",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      color: "transparent",
    }}
  >
    CC
  </span>
);

const getModelLabel = (model: AI_MODEL, shouldShowBadge = false) => {
  const config = MODEL_CONFIG_MAP[model] || MODEL_CONFIG_MAP[AI_MODEL.AUTO]!;

  return (
    <div className="flex items-center gap-1">
      <Icon
        icon={config.icon}
        className="h-3 w-3 text-secondary-900 shrink-0 mr-0.5"
      />
      <span className="truncate">{config.label}</span>
      {shouldShowBadge && <ClaudeCodeBadge />}
    </div>
  );
};

// Local interface for dropdown menu items (includes value property)
interface DropdownModelItem {
  value: AI_MODEL;
  label: string;
  icon: (typeof ICONS)[keyof typeof ICONS];
  tooltip?: string;
}

const REGULAR_MODELS: DropdownModelItem[] = [
  AI_MODEL.AUTO,
  AI_MODEL.CLAUDE_4_6_OPUS,
  AI_MODEL.CLAUDE_4_6_SONNET,
  AI_MODEL.CLAUDE_4_5_HAIKU,
  AI_MODEL.GEMINI_3_1_PRO,
  AI_MODEL.GPT_52,
  AI_MODEL.GPT_53_CODEX,
].map((model) => ({
  value: model,
  label: MODEL_CONFIG_MAP[model]?.label || "",
  icon: MODEL_CONFIG_MAP[model]?.icon || ICONS.CLAUDE,
  tooltip: MODEL_CONFIG_MAP[model]?.tooltip,
}));

interface AiModelDropdownProps {
  codeGenerationModel: AI_MODEL;
  className?: string;
}

const AiModelDropdown = ({
  codeGenerationModel,
  className,
}: AiModelDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const agent = useAiActionStore((state) => state.agent);
  const setAgent = useAiActionStore((state) => state.setAgent);

  const canvasId = useCanvasId();
  const { isClaudeCodeSession } = useClaudeCodeSessions(canvasId);
  const { subscriptionTier } = useSubscriptionInfo();

  const selectedConversationId = useAiActionStore(
    (state) => state.selectedConversationId,
  );

  const isFreeUser = subscriptionTier === "free";
  const isClaudeCodeDisabled = isFreeUser;

  // Show badge when using Claude Agent
  const shouldShowBadge = agent === AI_AGENT.CLAUDE_AGENT;

  const isModelAvailable = (model: AI_MODEL) => {
    return AGENT_SUPPORTED_MODELS[AI_AGENT.MAX_AGENT].has(model);
  };

  const renderModelLabel = (model: DropdownModelItem) => {
    return (
      <div className="flex items-center">
        <Icon icon={model.icon} className="h-4 w-4 text-secondary-900 mr-1" />
        <span>{model.label}</span>
      </div>
    );
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DropdownMenuTrigger asChild>
        <div
          data-testid="model-selector-trigger"
          className={cn(
            "h-7 p-0 min-w-fit flex items-center text-xs justify-between rounded-md transition-colors bg-secondary-n-950 px-2 cursor-pointer gap-1",
            isOpen ? "text-secondary-n-50" : "text-text-tertiary",
            className,
          )}
        >
          {getModelLabel(
            isFreeUser ? AI_MODEL.AUTO : codeGenerationModel,
            shouldShowBadge,
          )}
          {isOpen ? (
            <Lucide.ChevronUp className="h-4 w-4" />
          ) : (
            <Lucide.ChevronDown className="h-4 w-4" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[240px] rounded-lg px-0 pb-2 pt-2 bg-popover text-xs text-white overflow-visible"
      >
        {/* Models List */}
        <div className="px-2">
          {/* Claude Agent - Collapsible */}
          <ClaudeAgentSection
            agent={agent}
            codeGenerationModel={codeGenerationModel}
            setAgent={setAgent}
            setIsOpen={setIsOpen}
            isClaudeCodeDisabled={isClaudeCodeDisabled}
          />

          {/* Regular Models */}
          {REGULAR_MODELS.map((model) => {
            const isLocked = isFreeUser && model.value !== AI_MODEL.AUTO;
            return (
              <MenuItem
                key={model.value}
                value={model.value}
                agent={AI_AGENT.MAX_AGENT}
                label={renderModelLabel(model)}
                isSelected={
                  isFreeUser
                    ? model.value === AI_MODEL.AUTO
                    : agent !== AI_AGENT.CLAUDE_AGENT &&
                      codeGenerationModel === model.value
                }
                onClose={() => setIsOpen(false)}
                disabled={!isModelAvailable(model.value)}
                tooltip={model.tooltip}
                lockedTooltip={isLocked ? "Available on paid plan" : undefined}
              />
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AiModelDropdown;
